import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle,
  PaperPlaneTilt,
  UploadSimple,
  Paperclip,
  X,
} from "@phosphor-icons/react";
import { ColorPicker } from "@/components/ColorPicker";
import { PortalShell } from "@/components/PortalShell";
import { portalApi, portalAsset } from "@/lib/portalApi";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { shrinkImage } from "@/lib/shrinkImage";

const PROJECT_TYPES = [
  "Kitchen",
  "Bathroom",
  "Commercial",
  "Fireplace",
  "Outdoor",
  "Other",
];
const TIMELINES = [
  "ASAP",
  "2-4 weeks",
  "1-3 months",
  "3+ months",
  "Just planning",
];

const label =
  "font-mono-data text-[10px] uppercase tracking-[0.22em] text-slate-500";
const field =
  "h-12 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-teal-500";

export default function PortalRequestQuote() {
  const [params] = useSearchParams();
  // Pre-fill from the public Measure tool: /portal/request?slab=…&color=…&item=…&size=…&area=…
  const piece = params.get("size")
    ? {
        slab: params.get("slab") || "",
        color: params.get("color") || "",
        item: params.get("item") || "",
        size: params.get("size") || "",
        area: params.get("area") || "",
        length: params.get("length") || "",
        width: params.get("width") || "",
        photo: params.get("photo") || "",
      }
    : null;
  const [me, setMe] = useState(null);
  const [colors, setColors] = useState([]);
  const [form, setForm] = useState({
    address: "",
    project_type: "Kitchen",
    timeline: "ASAP",
    phone: "",
    working_with: "",
    colors: piece?.color ? [piece.color] : [],
    message: piece
      ? `Piece measured on ${piece.color}${piece.item ? ` (item ${piece.item})` : ""}: ${piece.size}${
          piece.area ? ` — about ${piece.area} ft²` : ""
        }. Please quote this piece.`
      : "",
    files: [],
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    portalApi
      .get("/me")
      .then(({ data }) => {
        setMe(data);
        setForm((f) => ({
          ...f,
          phone: data.customer?.phone || "",
          address: data.customer?.address || "",
        }));
      })
      .catch(() => {});
    api
      .get("/public/lead-form")
      .then(({ data }) => setColors(data.colors || []))
      .catch(() => setColors([]));
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    for (const file of list) {
      const body = new FormData();
      body.append("file", await shrinkImage(file));
      try {
        const res = await fetch(`${API}/public/leads/upload`, {
          method: "POST",
          body,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Upload failed");
        setForm((f) => ({ ...f, files: [...f.files, data] }));
        toast.success(`${file.name} attached`);
      } catch (err) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await portalApi.post("/quote-request", {
        ...form,
        piece: piece || undefined,
      });
      setDone(data.code);
      toast.success("Thank you — we have your request");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not send that request",
      );
    }
    setBusy(false);
  };

  if (done)
    return (
      <PortalShell
        back="/portal/home"
        micro="Request new quote"
        title="We're on it"
      >
        <div
          className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6"
          data-testid="portal-request-done"
        >
          <div className="flex items-center gap-2 text-emerald-700">
            <CheckCircle size={18} weight="bold" />
            <span className="text-sm font-semibold">
              Request {done} received
            </span>
          </div>
          <p className="mt-2 text-sm text-emerald-700">
            One of us will call you about {form.project_type.toLowerCase()} at{" "}
            {form.address}. Your quote shows up right here once it is ready.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to="/portal/home"
              data-testid="portal-request-home"
              className="rounded-lg bg-[#c39e6d] px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-white hover:bg-[#a9855a]"
            >
              Back to my portal
            </Link>
            <Link
              to="/portal/inventory"
              data-testid="portal-request-inventory"
              className="rounded-lg border border-slate-200 px-5 py-3 text-[11px] font-bold uppercase tracking-widest text-slate-600 hover:text-slate-900"
            >
              Browse the inventory
            </Link>
          </div>
        </div>
      </PortalShell>
    );

  return (
    <PortalShell
      back="/portal/home"
      micro="Request new quote"
      title="Tell us about the project"
      subtitle="A few details and we will get a price back to you — no obligation."
    >
      <div
        className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]"
        data-testid="portal-quote-form"
      >
        <div className="space-y-5 rounded-xl border border-slate-200 bg-white p-6">
          {piece && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border border-teal-500/40 bg-teal-50 px-4 py-3 text-sm text-teal-800"
              data-testid="portal-request-prefill"
            >
              {piece.photo && (
                <img
                  src={portalAsset(`/inventory/photo/${piece.photo}?w=160`)}
                  alt={piece.color}
                  data-testid="portal-request-prefill-photo"
                  className="h-14 w-14 shrink-0 rounded-md object-cover ring-1 ring-teal-500/30"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <span className="font-semibold">
                Pre-filled from the Measure tool:
              </span>
              <span>
                {piece.color}
                {piece.item ? ` · item ${piece.item}` : ""} · {piece.size}
                {piece.area ? ` · ${piece.area} ft²` : ""}
              </span>
              {piece.slab && (
                <Link
                  to={`/inventory/${piece.slab}`}
                  data-testid="portal-request-prefill-link"
                  className="ml-auto text-xs font-semibold uppercase tracking-wider text-teal-700 underline-offset-2 hover:underline"
                >
                  View slab
                </Link>
              )}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <span className={label}>Job address</span>
              <input
                data-testid="portal-request-address"
                value={form.address}
                onChange={(e) => set({ address: e.target.value })}
                placeholder="142 Elm St, Springfield"
                className={field}
              />
            </div>
            <div className="space-y-1.5">
              <span className={label}>Best number</span>
              <input
                data-testid="portal-request-phone"
                value={form.phone}
                onChange={(e) => set({ phone: e.target.value })}
                placeholder="(555) 010-2030"
                className={field}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <span className={label}>What are we doing?</span>
            <div className="flex flex-wrap gap-2">
              {PROJECT_TYPES.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`portal-request-type-${p.toLowerCase()}`}
                  onClick={() => set({ project_type: p })}
                  className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    form.project_type === p
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-slate-50 text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <span className={label}>When do you need it?</span>
            <div className="flex flex-wrap gap-2">
              {TIMELINES.map((t) => (
                <button
                  key={t}
                  type="button"
                  data-testid={`portal-request-timeline-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => set({ timeline: t })}
                  className={`rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                    form.timeline === t
                      ? "bg-slate-900 text-white"
                      : "border border-slate-200 bg-slate-50 text-slate-500 hover:text-slate-900"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <span className={label}>Working with (builder, designer…)</span>
            <input
              data-testid="portal-request-working-with"
              value={form.working_with}
              onChange={(e) => set({ working_with: e.target.value })}
              placeholder="Who else is on the project?"
              className={field}
            />
          </div>

          <div className="space-y-1.5">
            <span className={label}>Colors you like</span>
            <ColorPicker
              testId="portal-request-colors"
              options={colors}
              value={form.colors}
              onChange={(next) => set({ colors: next })}
            />
          </div>

          <div className="space-y-1.5">
            <span className={label}>Anything else</span>
            <textarea
              data-testid="portal-request-message"
              value={form.message}
              onChange={(e) => set({ message: e.target.value })}
              rows={4}
              placeholder="Sizes, sink, edge you have in mind, photos we should see…"
              className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-teal-500"
            />
          </div>

          <div className="space-y-1.5">
            <span className={label}>Photos and plans</span>
            <label
              data-testid="portal-request-upload-zone"
              className="flex cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-900/40 px-6 py-8 text-center transition-colors duration-150 hover:border-teal-400 hover:bg-white"
            >
              <UploadSimple
                size={22}
                weight="bold"
                className="text-teal-700"
              />
              <span className="mt-2 text-sm font-semibold text-slate-700">
                {uploading ? "Uploading…" : "Add photos, sketches or a plan"}
              </span>
              <span className="mt-1 text-[11px] text-slate-500">
                JPG, PNG, HEIC or PDF · up to 12 MB each
              </span>
              <input
                data-testid="portal-request-upload-input"
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.webp,.heic,.gif,.pdf"
                className="hidden"
                onChange={(e) => uploadFiles(e.target.files)}
              />
            </label>
            {form.files.length > 0 && (
              <div
                className="space-y-2 pt-2"
                data-testid="portal-request-files"
              >
                {form.files.map((file) => (
                  <div
                    key={file.id}
                    data-testid={`portal-request-file-${file.id}`}
                    className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
                  >
                    <Paperclip
                      size={14}
                      weight="bold"
                      className="text-slate-500"
                    />
                    <span className="min-w-0 flex-1 truncate text-xs text-slate-600">
                      {file.name}
                    </span>
                    <span className="font-mono-data shrink-0 text-[10px] text-slate-500">
                      {Math.max(1, Math.round((file.size || 0) / 1024))} KB
                    </span>
                    <button
                      type="button"
                      data-testid={`portal-request-file-remove-${file.id}`}
                      onClick={() =>
                        set({
                          files: form.files.filter((x) => x.id !== file.id),
                        })
                      }
                      className="text-slate-400 transition-colors duration-150 hover:text-red-600"
                    >
                      <X size={14} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            data-testid="portal-request-submit"
            disabled={busy || form.address.trim().length < 3}
            onClick={send}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#c39e6d] text-[11px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-[#a9855a] disabled:opacity-40"
          >
            <PaperPlaneTilt size={15} weight="bold" />
            {busy ? "Sending…" : "Send my request"}
          </button>
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className={label}>Your details</div>
            <p className="mt-3 text-sm font-semibold text-slate-900">
              {me?.customer?.name || me?.user?.email}
            </p>
            <p className="mt-1 text-xs text-slate-500">{me?.user?.email}</p>
            <p className="mt-3 text-xs leading-relaxed text-slate-500">
              We already have you on file — this request comes through with your
              name on it.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className={label}>Seen something you like?</div>
            <Link
              to="/portal/inventory"
              data-testid="portal-request-see-stock"
              className="mt-3 inline-flex text-sm font-semibold text-teal-700 hover:text-teal-800"
            >
              Browse the inventory →
            </Link>
          </div>
        </aside>
      </div>
    </PortalShell>
  );
}
